import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lightbulb, X } from "lucide-react";
import type { HelpTopic } from "@/lib/help-content";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

function todayStr(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function readHide(userId: string | null, storageKey: string): string | null {
  if (typeof window === "undefined") return null;
  const uid = userId ?? "anon";
  try {
    return window.localStorage.getItem(`ezyclass.guide_hide.${uid}.${storageKey}`);
  } catch {
    return null;
  }
}

function writeHide(userId: string | null, storageKey: string, value: string) {
  if (typeof window === "undefined") return;
  const uid = userId ?? "anon";
  try {
    window.localStorage.setItem(`ezyclass.guide_hide.${uid}.${storageKey}`, value);
  } catch {
    /* ignore */
  }
}

export function PageGuide({
  topic,
  storageKey,
  className,
}: {
  topic: HelpTopic;
  storageKey: string;
  className?: string;
}) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [forceShow, setForceShow] = React.useState(false);
  const [hidden, setHidden] = React.useState<boolean>(true); // default hidden until mount-time check
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    const v = readHide(userId, storageKey);
    if (v === "forever") setHidden(true);
    else if (v === todayStr()) setHidden(true);
    else setHidden(false);
    setMounted(true);
  }, [userId, storageKey]);

  const visible = mounted && (forceShow || !hidden);

  return (
    <div className={cn("mb-4", className)}>
      <div className="flex justify-end mb-2">
        <button
          type="button"
          onClick={() => setForceShow(true)}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 hover:underline"
          aria-label="Xem hướng dẫn"
        >
          <Lightbulb className="size-3.5" strokeWidth={2} />
          Xem hướng dẫn
        </button>
      </div>

      <AnimatePresence initial={false}>
        {visible && (
          <motion.div
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
            className="overflow-hidden"
          >
            <div className="rounded-2xl border border-amber-200 dark:border-amber-500/30 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-500/10 dark:to-yellow-500/5 p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="size-9 shrink-0 rounded-xl bg-amber-100 dark:bg-amber-500/20 grid place-items-center">
                  <Lightbulb className="size-5 text-amber-600 dark:text-amber-300" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display font-semibold text-slate-900 dark:text-white">{topic.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">{topic.summary}</p>

                  {topic.steps && topic.steps.length > 0 && (
                    <ol className="mt-3 space-y-1.5 text-sm text-slate-700 dark:text-slate-200 list-decimal pl-5">
                      {topic.steps.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ol>
                  )}

                  {topic.tips && topic.tips.length > 0 && (
                    <ul className="mt-3 space-y-1 text-xs text-slate-600 dark:text-slate-400 list-disc pl-5">
                      {topic.tips.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        writeHide(userId, storageKey, todayStr());
                        setForceShow(false);
                        setHidden(true);
                      }}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 ring-1 ring-amber-200 dark:ring-amber-500/30 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors"
                    >
                      Ẩn hôm nay
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        writeHide(userId, storageKey, "forever");
                        setForceShow(false);
                        setHidden(true);
                      }}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                    >
                      <X className="size-3" strokeWidth={2} />
                      Không hiện lại
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
